(() => {
  if (customElements.get('desktop-mini-cart')) return;

  customElements.define('desktop-mini-cart', class DesktopMiniCart extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.rewards = this.querySelector('cart-rewards');
      this.items = this.querySelector('[data-mini-items]');
      this.status = this.querySelector('[data-mini-status]');
      this.addEventListener('rewards:render', ({ detail }) => this.render(detail.state, detail.config), options);
      this.addEventListener('rewards:availability', ({ detail }) => {
        this.hidden = !detail.available;
        this.updateHeight();
        this.updateVisibility();
      }, options);
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-mini-remove]');
        if (remove) this.removeItem(remove);
        const arrow = event.target.closest('[data-mini-next], [data-mini-prev]');
        if (arrow) {
          const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
          const step = arrow.matches('[data-mini-prev]') ? -1 : 1;
          this.items.scrollBy({ left: this.items.clientWidth * direction * step, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      }, options);
      document.addEventListener('cart:bundled-sections', ({ detail }) => {
        if (!detail.sections.includes(this.dataset.sectionId)) detail.sections.push(this.dataset.sectionId);
      }, options);
      this.items.addEventListener('scroll', () => this.updateOverflow(), options);
      this.observer = new ResizeObserver(() => {
        this.updateHeight();
        this.updateOverflow();
        this.updateVisibility();
      });
      this.observer.observe(this);
      this.observer.observe(this.items);
      customElements.whenDefined('cart-rewards').then(() => {
        if (!this.isConnected || !this.rewards.rewardConfig) return;
        const config = this.rewards.rewardConfig;
        this.render(theme.cartRewards.getState(config), config);
      });
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.request?.abort();
      document.documentElement.style.removeProperty('--desktop-mini-cart-height');
    }

    updateHeight() {
      document.documentElement.style.setProperty('--desktop-mini-cart-height', `${this.hidden ? 0 : this.getBoundingClientRect().height}px`);
    }

    updateVisibility() {
      const visible = !this.hidden;
      this.classList.toggle('is-visible', !!visible);
      this.inert = !visible;
    }

    updateOverflow() {
      const products = this.items.closest('.desktop-mini-cart__products');
      products.classList.toggle('has-overflow', this.items.scrollWidth > products.clientWidth - 8 + 1);
      const position = Math.abs(this.items.scrollLeft);
      const remaining = this.items.scrollWidth - this.items.clientWidth - position;
      this.querySelector('[data-mini-prev]').hidden = position < 2;
      this.querySelector('[data-mini-next]').hidden = remaining < 2;
    }

    render(state, config) {
      this.hidden = !state.available;
      this.config = config;
      if (!state.available) {
        this.updateHeight();
        return;
      }
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency });
      const money = (cents) => formatter.format(cents / 100);
      const items = config.cart.items || [];
      const signature = JSON.stringify(items);
      if (signature !== this.itemsSignature) {
        const fragment = document.createDocumentFragment();
        items.forEach((item) => {
          const row = document.createElement('div');
          row.className = `desktop-mini-cart__item${item.available ? '' : ' is-sold-out'}`;
          row.setAttribute('role', 'listitem');
          if (item.image) {
            const image = document.createElement('img');
            image.className = 'desktop-mini-cart__image';
            image.src = item.image;
            image.alt = item.title;
            image.width = image.height = 60;
            row.append(image);
          }
          const quantity = document.createElement('span');
          quantity.className = item.available ? 'desktop-mini-cart__quantity' : 'desktop-mini-cart__sold-out';
          quantity.textContent = item.available ? `x${item.quantity}` : 'Sold Out';
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'desktop-mini-cart__remove';
          remove.dataset.miniRemove = item.key;
          remove.setAttribute('aria-label', `Remove ${item.title}`);
          remove.append(this.querySelector('[data-mini-remove-icon]').content.cloneNode(true));
          row.append(quantity, remove);
          fragment.append(row);
        });
        this.items.replaceChildren(fragment);
        this.itemsSignature = signature;
      }
      this.items.hidden = !items.length;
      this.querySelector('[data-mini-empty]').hidden = !!items.length;
      const shipping = state.tiers.find((tier) => /^free shipping$/i.test(tier.label.trim()));
      const emptyMessage = shipping ? `Shop More, Ship Free — ${money(shipping.threshold).replace(/([.,])00(?=\D*$)/, '')}+` : state.next?.label || config.complete;
      this.querySelector('[data-mini-empty-message]').textContent = emptyMessage;
      const total = config.cart.total_price;
      const original = config.cart.original_total_price;
      const saving = config.cart.total_discount;
      this.querySelector('[data-mini-total]').textContent = money(total);
      this.querySelector('[data-mini-saving]').textContent = money(saving);
      const compare = this.querySelector('[data-mini-original]');
      compare.textContent = money(original);
      compare.hidden = saving <= 0;
      this.querySelector('[data-mini-shipping]').hidden = !shipping || state.shippingTotal < shipping.threshold;
      this.querySelector('[data-mini-checkout]').disabled = !items.length || this.busy;
      this.querySelectorAll('[data-mini-remove]').forEach((button) => { button.disabled = !!this.busy; });
      this.updateHeight();
      this.updateOverflow();
      this.updateVisibility();
    }

    async removeItem(button) {
      if (this.busy || button.disabled) return;
      this.busy = true;
      this.status.textContent = '';
      const key = button.dataset.miniRemove;
      const index = [...this.items.children].indexOf(button.closest('[role="listitem"]'));
      const restoreFocus = document.activeElement === button;
      let removed = false;
      this.querySelectorAll('[data-mini-remove], [data-mini-checkout]').forEach((control) => { control.disabled = true; });
      button.closest('[role="listitem"]').classList.add('is-removing');
      this.request = new AbortController();
      const sections = [this.dataset.sectionId];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0, sections: [...new Set(sections)].slice(0, 5), sections_url: window.location.pathname }),
          signal: this.request.signal,
        });
        const cart = await response.json();
        if (!response.ok || cart.errors) throw new Error(cart.description || cart.message || 'The item could not be removed.');
        if (!this.isConnected) return;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'desktop-mini-cart', cart });
        this.status.textContent = 'Item removed from your cart.';
        removed = true;
      } catch (error) {
        if (error.name !== 'AbortError') this.status.textContent = error.message;
      } finally {
        this.busy = false;
        this.querySelector('.is-removing')?.classList.remove('is-removing');
        this.querySelectorAll('[data-mini-remove]').forEach((control) => { control.disabled = false; });
        this.querySelector('[data-mini-checkout]').disabled = !(this.config?.cart.items?.length);
        if (removed && restoreFocus && this.isConnected) {
          const buttons = this.querySelectorAll('[data-mini-remove]');
          (buttons[Math.min(index, buttons.length - 1)] || this.querySelector('[data-mini-empty]')).focus({ preventScroll: true });
        }
      }
    }
  });
})();
